import { useEffect } from 'react';
import getApiError from 'services/getApiError';
import { getSinglesTrack, LibraryItemAll } from 'services/singles';
import useSWR, { mutate } from 'swr';
import { getLibraryItem } from './useLibraryItemDetails';

/**
 * Used to get single track details using getSinglesTrack and getLibraryItem fn.
 * */
export const getSingleTrackDetails = async (singleTrackIdOrSlug?: string) => {
  if (!singleTrackIdOrSlug) {
    return null;
  }

  let singleTrackId: string;

  try {
    const response = await getSinglesTrack(singleTrackIdOrSlug);
    const { singleTrackDetails, isRemovedOrRejected } = response ?? {};

    if (isRemovedOrRejected) {
      return null;
    }

    // // Store single trackId
    // singleTrackId = singleTrackDetails?.id ?? singleTrackIdOrSlug;
    return singleTrackDetails;
  } catch (_e) {
    singleTrackId = singleTrackIdOrSlug;
  }

  // Pass single track id to the below function which provide single track details in json form.
  return getLibraryItem(singleTrackId);
};

interface PrefetchedSingleTrackEventDetail {
  slug: string;
  data?: LibraryItemAll;
}

export interface UseSingleTrackDetailsParams {
  singleTrackIdOrSlug?: string;
}

const getPrefetchedSingleTrackDetails = (
  singleTrackIdOrSlug?: string
): LibraryItemAll | undefined => {
  if (
    typeof window === 'undefined' ||
    !singleTrackIdOrSlug ||
    !window.__LIBRARY_ITEM_PREFETCH__
  ) {
    return undefined;
  }

  return window.__LIBRARY_ITEM_PREFETCH__[singleTrackIdOrSlug];
};

const getPrefetchPromise = (singleTrackIdOrSlug?: string) => {
  if (
    typeof window === 'undefined' ||
    !singleTrackIdOrSlug ||
    !window.__LIBRARY_ITEM_PREFETCH_PROMISES__
  ) {
    return undefined;
  }

  return window.__LIBRARY_ITEM_PREFETCH_PROMISES__[singleTrackIdOrSlug];
};

const useSingleTrackDetails = ({
  singleTrackIdOrSlug
}: UseSingleTrackDetailsParams) => {
  const prefetchedSingleTrackDetails =
    getPrefetchedSingleTrackDetails(singleTrackIdOrSlug);
  const cacheKey = singleTrackIdOrSlug
    ? `single_track_details/${singleTrackIdOrSlug}`
    : null;
  const { data, isValidating, error } = useSWR(
    cacheKey,
    () => {
      if (!singleTrackIdOrSlug) {
        return null;
      }

      const prefetchPromise = getPrefetchPromise(singleTrackIdOrSlug);

      if (prefetchPromise) {
        return prefetchPromise.then(prefetched => {
          if (prefetched !== undefined) {
            return prefetched;
          }

          return getSingleTrackDetails(singleTrackIdOrSlug);
        });
      }

      return getSingleTrackDetails(singleTrackIdOrSlug);
    },
    {
      initialData: prefetchedSingleTrackDetails,
      revalidateOnFocus: false,
      refreshWhenOffline: false,
      onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        const { response } = error ?? {};
        const { status } = response ?? {};

        // Never retry on 404.
        if (status === 404) return;

        // Only retry up to 3 times.
        if (retryCount && retryCount >= 3) return;

        // Retry
        revalidate({ retryCount });
      }
    }
  );
  const isFetchingSingleTrackDetails = data === undefined && isValidating;

  useEffect(() => {
    if (!cacheKey || typeof window === 'undefined') {
      return;
    }

    const applyPrefetchedData = (prefetched?: LibraryItemAll) => {
      if (!prefetched) {
        return;
      }

      mutate(cacheKey, prefetched, false);
    };

    applyPrefetchedData(getPrefetchedSingleTrackDetails(singleTrackIdOrSlug));

    const handlePrefetch = (event: Event) => {
      const customEvent =
        event as CustomEvent<PrefetchedSingleTrackEventDetail>;
      const { slug, data } = customEvent.detail || {};

      if (!slug || slug !== singleTrackIdOrSlug) {
        return;
      }

      applyPrefetchedData(data);
    };

    window.addEventListener('library-item-prefetched', handlePrefetch);

    return () => {
      window.removeEventListener('library-item-prefetched', handlePrefetch);
    };
  }, [cacheKey, singleTrackIdOrSlug]);

  return {
    isFetchingSingleTrackDetails,
    libraryItemDetails: data ?? undefined,
    error: getApiError(error)
  };
};

export default useSingleTrackDetails;
